import { loginSchema } from '@insightai/auth'
import { Button, Checkbox, Field, Input } from '@insightai/ui'
import { Link } from 'react-router-dom'
import { startPkce } from '../../auth/startPkce'
import { useZodForm } from '../../auth/useZodForm'
import { AuthLayout } from './AuthLayout'
import { PrototypeNotice } from './PrototypeNotice'
import { SocialButtons } from './SocialButtons'

/**
 * M00-15 — login: email/password, Google, Microsoft, Remember Me, Forgot Password.
 *
 * Remember Me does NOT extend a client-side token lifetime, because there is no
 * persistent client-side token (SEC-FR-001). It is passed to the IdP so the
 * *refresh cookie* is issued with a longer expiry. Any other implementation would
 * mean storing something durable in the browser, which the AC explicitly probes
 * for.
 *
 * Credentials are collected by Keycloak, not posted from this page: the browser
 * never handles a password against our own API.
 */
export function LoginPage() {
  const form = useZodForm(loginSchema, { email: '', password: '', rememberMe: false })

  const onSubmit = form.handleSubmit(async (values) => {
    // `password` is read only by the prototype's mock, and only to honour the
    // fixture's `accept_any_password` flag. The real PKCE path ignores it: the
    // password is collected by Keycloak, never posted from this page.
    await startPkce({ loginHint: values.email, password: values.password })
  })

  return (
    <AuthLayout
      title="Sign in to InsightAI"
      subtitle="Continue to your research workspace"
      footer={
        <>
          Don't have an account?{' '}
          <Link to="/auth/signup" className="text-text-link underline underline-offset-4">
            Create one
          </Link>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <PrototypeNotice
          onFill={({ email, password }) => {
            form.setField('email', email)
            form.setField('password', password)
          }}
        />

        <SocialButtons mode="signin" />

        <div className="flex items-center gap-3" aria-hidden>
          <span className="h-px flex-1 bg-border-subtle" />
          <span className="text-caption text-text-tertiary">or sign in with email</span>
          <span className="h-px flex-1 bg-border-subtle" />
        </div>

        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Field label="Work email" required error={form.errors.email}>
            <Input
              type="email"
              autoComplete="email"
              autoFocus
              value={form.values.email}
              onChange={(event) => form.setField('email', event.target.value)}
            />
          </Field>

          <Field label="Password" required error={form.errors.password}>
            <Input
              type="password"
              autoComplete="current-password"
              value={form.values.password}
              onChange={(event) => form.setField('password', event.target.value)}
            />
          </Field>

          <div className="flex items-center justify-between gap-3">
            <Checkbox
              label="Keep me signed in"
              checked={form.values.rememberMe ?? false}
              onCheckedChange={(checked) => form.setField('rememberMe', checked)}
            />
            <Link
              to="/auth/forgot-password"
              className="text-body-sm text-text-link underline underline-offset-4"
            >
              Forgot your password?
            </Link>
          </div>

          <Button type="submit" loading={form.submitting} loadingLabel="Redirecting to sign-in">
            Sign in
          </Button>
        </form>
      </div>
    </AuthLayout>
  )
}
